/* One table's two column charts: where each column sits between sparse and full, and between a code and
 * a key; and how each column's completeness moved since the previous profile.
 *
 * Both open a column in the column map below them, because a dot on a chart is a question about that
 * column and the map is where its answer is. */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { isDominated, share } from '@/features/profiler';
import type { ColumnDrift, ColumnSketch } from '@/shared/api/generated/catalog';
import { useMeasuredWidth } from '@/shared/ui/useMeasuredWidth';

import {
  COMPLETENESS_GOOD,
  COMPLETENESS_RISK,
  SHARE_TICKS,
  distinctText,
  dumbbell,
  shapeGroups,
  shareAxis,
} from '../model/estate';

/** A distinct share as the axis prints it: 0.001%, 0.1%, 10%, 100%. */
function shareTick(value: number): string {
  return `${(value * 100).toLocaleString(undefined, { maximumSignificantDigits: 3 })}%`;
}

export function ColumnShape({
  columns,
  onOpen,
}: {
  readonly columns: readonly ColumnSketch[];
  readonly onOpen: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [plotRef, width] = useMeasuredWidth<HTMLDivElement>(520);
  const { groups, unplotted } = shapeGroups(columns, isDominated, width);

  return (
    <article className="lq-pf__panel lq-pf__panel--shape">
      <div className="lq-pf__panelHead lq-pf__panelHead--wrap">
        <h2 className="lq-pf__panelTitle lq-pf__grow">{t('profiles.shape.title')}</h2>
        <span className="lq-pf__panelSub">{t('profiles.shape.sub')}</span>
      </div>
      <div className="lq-pf__shapeHints" aria-hidden="true">
        <span data-tone="risk">{t('profiles.shape.sparse')}</span>
        <span data-tone="good">{t('profiles.shape.keys')}</span>
      </div>
      <div
        ref={plotRef}
        className="lq-pf__plot lq-pf__plot--shape"
        role="group"
        aria-label={t('profiles.shape.label')}
      >
        <div className="lq-pf__shapeZone lq-pf__shapeZone--good" />
        <div className="lq-pf__shapeZone lq-pf__shapeZone--risk" />
        {SHARE_TICKS.map((value) => (
          <div
            key={value}
            className="lq-pf__rule lq-pf__rule--faint"
            style={{ bottom: `${shareAxis(value).toFixed(1)}%` }}
            aria-hidden="true"
          >
            <span className="lq-pf__ruleLabel lq-pf__ruleLabel--wide">
              {shareTick(value)}
            </span>
          </div>
        ))}
        <span className="lq-pf__axisTick lq-pf__axisTick--start" aria-hidden="true">
          0%
        </span>
        <span
          className="lq-pf__axisTick"
          data-tone="risk"
          style={{ left: `${String(COMPLETENESS_RISK * 100)}%` }}
          aria-hidden="true"
        >
          {`${String(COMPLETENESS_RISK * 100)}%`}
        </span>
        <span
          className="lq-pf__axisTick"
          data-tone="good"
          style={{ left: `${String(COMPLETENESS_GOOD * 100)}%` }}
          aria-hidden="true"
        >
          {`${String(COMPLETENESS_GOOD * 100)}%`}
        </span>
        <span className="lq-pf__axisTick lq-pf__axisTick--end" aria-hidden="true">
          100%
        </span>
        {groups.map((group) => {
          const described = group.sketches.map((sketch) =>
            t(
              isDominated(sketch)
                ? 'profiles.shape.pointDominated'
                : 'profiles.shape.point',
              {
                column: sketch.column,
                completeness: share(sketch.completeness),
                distinct: distinctText(sketch),
              },
            ),
          );
          return (
            <button
              key={group.columns.join('\u0000')}
              type="button"
              className="lq-pf__point"
              data-flip={group.flip}
              data-lane={group.lane}
              title={described.join('\n')}
              aria-label={described.join('; ')}
              style={{
                left: `${group.x.toFixed(2)}%`,
                bottom: `${group.y.toFixed(2)}%`,
              }}
              onClick={() => {
                onOpen(group.columns[0] ?? '');
              }}
            >
              <span
                className="lq-pf__pointMark"
                data-fill={group.tone}
                data-dominated={group.dominated}
              />
              <span className="lq-pf__pointLabel" aria-hidden="true">
                {group.columns.join(', ')}
              </span>
            </button>
          );
        })}
      </div>
      <p className="lq-pf__plotNote">
        {t('profiles.shape.note')}
        {unplotted > 0 && <> {t('profiles.shape.unplotted', { count: unplotted })}</>}
      </p>
    </article>
  );
}

export function CompletenessChange({
  columns,
  drift,
  onOpen,
}: {
  readonly columns: readonly ColumnSketch[];
  readonly drift: readonly ColumnDrift[] | null;
  readonly onOpen: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const chart = dumbbell(columns, drift);

  return (
    <article className="lq-pf__panel lq-pf__panel--bells">
      <div className="lq-pf__panelHead lq-pf__panelHead--wrap">
        <h2 className="lq-pf__panelTitle lq-pf__grow">{t('profiles.bells.title')}</h2>
        <span className="lq-pf__bellKeys">
          <span className="lq-pf__bellKey">
            <span className="lq-pf__bellKeyDot lq-pf__bellKeyDot--before" />
            {t('profiles.bells.previous')}
          </span>
          <span className="lq-pf__bellKey">
            <span className="lq-pf__bellKeyDot lq-pf__bellKeyDot--after" />
            {t('profiles.bells.now')}
          </span>
        </span>
      </div>
      {chart === null ? (
        <p className="lq-pf__changesNote">{t('profiles.changes.none')}</p>
      ) : (
        <div className="lq-pf__bells">
          {chart.rows.map((row) => (
            <button
              key={row.column}
              type="button"
              className="lq-pf__bell"
              data-direction={row.direction}
              title={t('profiles.bells.row', {
                column: row.column,
                before: share(row.before),
                after: share(row.after),
              })}
              onClick={() => {
                onOpen(row.column);
              }}
            >
              <span className="lq-pf__bellName">{row.column}</span>
              <span className="lq-pf__bellTrack" aria-hidden="true">
                <span className="lq-pf__bellLine" />
                <span
                  className="lq-pf__bellSpan"
                  data-grow=""
                  style={{
                    left: `${row.from.toFixed(2)}%`,
                    width: `${row.width.toFixed(2)}%`,
                  }}
                />
                <span
                  className="lq-pf__bellBefore"
                  style={{ left: `${row.beforeAt.toFixed(2)}%` }}
                />
                <span
                  className="lq-pf__bellAfter"
                  style={{ left: `${row.afterAt.toFixed(2)}%` }}
                />
              </span>
              <span className="lq-pf__bellDelta">
                {row.direction === 'flat'
                  ? t('profiles.bells.flat')
                  : t(`profiles.bells.${row.direction}`, {
                      points: row.points.toFixed(1),
                    })}
              </span>
            </button>
          ))}
          <div className="lq-pf__bellAxis" aria-hidden="true">
            <span />
            <span className="lq-pf__bellTicks">
              {chart.ticks.map((tick) => (
                <span
                  key={tick}
                  className="lq-pf__bellTick"
                  style={{ left: `${chart.at(tick).toFixed(2)}%` }}
                >
                  {`${String(Math.round(tick * 100))}%`}
                </span>
              ))}
            </span>
            <span className="lq-pf__bellUnit">{t('profiles.bells.points')}</span>
          </div>
        </div>
      )}
    </article>
  );
}
